import type {
  WorkflowGraphCreateInput,
  WorkflowGraphCreateTrigger,
  WorkflowNodeInput,
} from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';
import type { WorkflowDraftState } from './workflow-draft-graph';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

type GraphCreateWebhookProviderKey = NonNullable<
  Extract<WorkflowGraphCreateTrigger, { kind: 'webhook' }>['providerKey']
>;

type DraftTriggerBinding =
  WorkflowDraftState['graph']['triggerBindings'][number];

/**
 * Copies the per-binding fields onto the serialized trigger.
 *
 * `enabled` is the canvas card's on/off switch. It has to travel with every
 * trigger because a graph save deletes and re-inserts the whole
 * `workflow_trigger_workflows` set — omit it and the next autosave turns a
 * trigger the user switched off back on.
 */
function withBindingFields(
  trigger: WorkflowGraphCreateTrigger,
  binding: DraftTriggerBinding
): WorkflowGraphCreateTrigger {
  return {
    ...trigger,
    ...(binding.defaultInput !== undefined && {
      defaultInput: binding.defaultInput,
    }),
    enabled: binding.enabled,
  };
}

function resolveDraftTriggerForBinding(
  draft: WorkflowDraftState,
  binding: DraftTriggerBinding,
  baseline?: WorkflowDraftState
): WorkflowGraphCreateTrigger | undefined {
  const snapshot = draft.graph.triggers.find(
    (entry) => entry.trigger.id === binding.triggerId
  );
  if (snapshot === undefined) {
    return undefined;
  }

  const pendingWebhook = draft.pendingWebhookCreates.get(snapshot.trigger.id);
  if (pendingWebhook !== undefined) {
    return {
      kind: 'webhook',
      name: pendingWebhook.name,
      ...(pendingWebhook.providerKey !== undefined && {
        providerKey:
          pendingWebhook.providerKey as GraphCreateWebhookProviderKey,
      }),
      ...(pendingWebhook.verificationConfig !== undefined && {
        verificationConfig: pendingWebhook.verificationConfig,
      }),
      ...(pendingWebhook.eventKey !== undefined && {
        eventKey: pendingWebhook.eventKey,
      }),
      ...(pendingWebhook.eventConfig !== undefined && {
        eventConfig: pendingWebhook.eventConfig,
      }),
      ...(pendingWebhook.endpointSecret !== undefined && {
        endpointSecret: pendingWebhook.endpointSecret,
      }),
    };
  }

  if (snapshot.kind === PersistedWorkflowTriggerKind.Cron) {
    const baselineSnapshot = baseline?.graph.triggers.find(
      (entry) => entry.trigger.id === snapshot.trigger.id
    );
    if (baselineSnapshot?.kind === PersistedWorkflowTriggerKind.Cron) {
      return {
        kind: 'existing',
        triggerId: snapshot.trigger.id,
      };
    }
    return {
      kind: 'cron',
      name: snapshot.trigger.name,
      schedule: snapshot.trigger.cron,
      timezone: snapshot.trigger.timezone,
    };
  }

  if (snapshot.kind === PersistedWorkflowTriggerKind.Webhook) {
    const baselineSnapshot = baseline?.graph.triggers.find(
      (entry) => entry.trigger.id === snapshot.trigger.id
    );
    if (
      snapshot.trigger.installStatus === 'draft' &&
      baselineSnapshot === undefined
    ) {
      return {
        kind: 'webhook',
        name: snapshot.trigger.name,
        ...(snapshot.trigger.providerKey !== undefined && {
          providerKey: snapshot.trigger
            .providerKey as GraphCreateWebhookProviderKey,
        }),
        ...(snapshot.trigger.verificationConfig !== undefined && {
          verificationConfig: snapshot.trigger.verificationConfig,
        }),
        ...(snapshot.trigger.eventKey !== undefined && {
          eventKey: snapshot.trigger.eventKey,
        }),
        ...(snapshot.trigger.eventConfig !== undefined && {
          eventConfig: snapshot.trigger.eventConfig,
        }),
        ...(snapshot.trigger.secret !== undefined &&
          snapshot.trigger.secret !== '' && {
            endpointSecret: snapshot.trigger.secret,
          }),
      };
    }

    if (
      baselineSnapshot?.kind === PersistedWorkflowTriggerKind.Webhook &&
      snapshot.kind === PersistedWorkflowTriggerKind.Webhook
    ) {
      const webhook: NonNullable<
        Extract<WorkflowGraphCreateTrigger, { kind: 'existing' }>['webhook']
      > = {};
      if (snapshot.trigger.name !== baselineSnapshot.trigger.name) {
        webhook.name = snapshot.trigger.name;
      }
      if (snapshot.trigger.enabled !== baselineSnapshot.trigger.enabled) {
        webhook.webhookRowEnabled = snapshot.trigger.enabled;
      }
      if (
        snapshot.trigger.providerKey !== baselineSnapshot.trigger.providerKey
      ) {
        if (snapshot.trigger.providerKey !== undefined) {
          webhook.providerKey = snapshot.trigger
            .providerKey as GraphCreateWebhookProviderKey;
        }
      }
      if (
        JSON.stringify(snapshot.trigger.verificationConfig) !==
        JSON.stringify(baselineSnapshot.trigger.verificationConfig)
      ) {
        if (snapshot.trigger.verificationConfig !== undefined) {
          webhook.verificationConfig = snapshot.trigger.verificationConfig;
        }
      }
      if (snapshot.trigger.eventKey !== baselineSnapshot.trigger.eventKey) {
        if (snapshot.trigger.eventKey !== undefined) {
          webhook.eventKey = snapshot.trigger.eventKey;
        }
      }
      if (
        JSON.stringify(snapshot.trigger.eventConfig) !==
        JSON.stringify(baselineSnapshot.trigger.eventConfig)
      ) {
        if (snapshot.trigger.eventConfig !== undefined) {
          webhook.eventConfig = snapshot.trigger.eventConfig;
        }
      }
      if (Object.keys(webhook).length > 0) {
        return {
          kind: 'existing',
          triggerId: snapshot.trigger.id,
          webhook,
        };
      }
    }

    return {
      kind: 'existing',
      triggerId: snapshot.trigger.id,
    };
  }

  if (snapshot.kind === PersistedWorkflowTriggerKind.Event) {
    const baselineSnapshot = baseline?.graph.triggers.find(
      (entry) => entry.trigger.id === snapshot.trigger.id
    );
    if (baselineSnapshot?.kind === PersistedWorkflowTriggerKind.Event) {
      return {
        kind: 'existing',
        triggerId: snapshot.trigger.id,
      };
    }
    return {
      kind: 'event',
      name: snapshot.trigger.name,
      eventName: snapshot.trigger.eventName,
    };
  }

  if (snapshot.kind === PersistedWorkflowTriggerKind.Dataset) {
    const baselineSnapshot = baseline?.graph.triggers.find(
      (entry) => entry.trigger.id === snapshot.trigger.id
    );
    if (baselineSnapshot?.kind === PersistedWorkflowTriggerKind.Dataset) {
      return {
        kind: 'existing',
        triggerId: snapshot.trigger.id,
      };
    }
    return {
      kind: 'dataset',
      name: snapshot.trigger.name,
      datasetId: snapshot.trigger.datasetId,
      criterion: snapshot.trigger.criterion,
      trackFields:
        snapshot.trigger.trackFields === null
          ? null
          : [...snapshot.trigger.trackFields],
      filter: snapshot.trigger.filter,
      processExistingRows: snapshot.trigger.processExistingRows,
      runMode: snapshot.trigger.runMode,
    };
  }

  return Assert.unreachable(snapshot);
}

export function draftStateToGraphCreateInput(
  draft: WorkflowDraftState,
  baseline?: WorkflowDraftState
): WorkflowGraphCreateInput {
  // Every binding, disabled ones included: a disabled binding is a trigger the
  // user switched *off*, not one they removed. Removal drops the binding from
  // `triggerBindings` outright (see `draftArchiveTriggerBinding`), so filtering
  // on `enabled` here would archive the trigger instead of pausing it.
  const triggers = draft.graph.triggerBindings.flatMap((binding) => {
    const trigger = resolveDraftTriggerForBinding(draft, binding, baseline);
    // Applied here rather than in each of the resolver's branches, so a new
    // trigger kind can't ship without its binding fields.
    return trigger === undefined ? [] : [withBindingFields(trigger, binding)];
  });

  return {
    nodes: draft.graph.nodes.map((node) => ({
      id: node.id,
      node: {
        type: node.type,
        data: node.data,
      } as WorkflowNodeInput,
      inputShape: node.inputShape,
    })),
    edges: draft.graph.edges.map((edge) => ({
      fromNodeId: edge.fromNodeId,
      toNodeId: edge.toNodeId,
    })),
    ...(triggers[0] !== undefined && { trigger: triggers[0] }),
    ...(triggers.length > 0 && { triggers }),
  };
}
